import { Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { EntityChangedEventArgs, EntityManager } from '@cime/breeze-client';
import { VesselNotificationPermissions, VesselPermissions } from '@common/classes/permissions';
import { AppControlComponent } from '@common/components/app-control/app-control.component';
import { User } from '@common/models/User';
import { ViewMode } from '@common/models/view-mode';
import { BreezeViewService } from '@common/services/breeze-view.service';
import { DialogService } from '@common/services/dialog.service';
import { SharedService } from '@common/services/shared.service';
import { TranslateService } from '@ngx-translate/core';
import { AutoCompleteComponent } from '@progress/kendo-angular-dropdowns';
import { SelectEvent, TabStripComponent } from '@progress/kendo-angular-layout';
import { VesselNotificationCategoryCodes } from 'app/vessel-notification/classes/vessel-notification-category-codes';
import { ImportIhsVesselModalComponent } from 'app/vessel/components/import-ihs-vessel-modal/import-ihs-vessel-modal.component';
import _ from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

interface ITabPermission {
    viewPermission: any;
    editPermission: any;
    viewProperty: string;
    editProperty: string;
    ruleSet: string;
}

@Component({
    selector: 'app-vessel-notification-view-content',
    templateUrl: './vessel-notification-view-content.component.html',
    styleUrls: ['./vessel-notification-view-content.component.scss']
})
export class VesselNotificationViewContentComponent implements OnInit {

    static tabPermissions: { [index: number]: ITabPermission } = {
        0: {
            viewPermission: VesselNotificationPermissions,
            editPermission: VesselNotificationPermissions,
            viewProperty: null,
            editProperty: null,
            ruleSet: VesselNotificationCategoryCodes.Voyage
        },
        1: {
            viewPermission: VesselNotificationPermissions.View.bunkers,
            editPermission: VesselNotificationPermissions.Edit.bunkers,
            viewProperty: 'viewBunkers',
            editProperty: 'editBunkers',
            ruleSet: VesselNotificationCategoryCodes.Bunkers
        },
        2: {
            viewPermission: VesselNotificationPermissions.View.dangerousGoods,
            editPermission: VesselNotificationPermissions.Edit.dangerousGoods,
            viewProperty: 'viewDangerousGoods',
            editProperty: 'editDangerousGoods',
            ruleSet: VesselNotificationCategoryCodes.DangerousGoods
        },
        3: {
            viewPermission: VesselNotificationPermissions.View.cargo,
            editPermission: VesselNotificationPermissions.Edit.cargo,
            viewProperty: 'viewCargo',
            editProperty: 'editCargo',
            ruleSet: VesselNotificationCategoryCodes.Cargo
        },
        4: {
            viewPermission: VesselNotificationPermissions.View.storeProducts,
            editPermission: VesselNotificationPermissions.Edit.storeProducts,
            viewProperty: 'viewStoreProducts',
            editProperty: 'editStoreProducts',
            ruleSet: VesselNotificationCategoryCodes.StoreProducts
        },
        5: {
            viewPermission: VesselNotificationPermissions.View.workers,
            editPermission: VesselNotificationPermissions.Edit.workers,
            viewProperty: 'viewWorkers',
            editProperty: 'editWorkers',
            ruleSet: VesselNotificationCategoryCodes.Workers
        },
        6: {
            viewPermission: VesselNotificationPermissions.View.passengers,
            editPermission: VesselNotificationPermissions.Edit.passengers,
            viewProperty: 'viewPassengers',
            editProperty: 'editPassengers',
            ruleSet: VesselNotificationCategoryCodes.Passengers
        },
        7: {
            viewPermission: VesselNotificationPermissions.View.security,
            editPermission: VesselNotificationPermissions.Edit.security,
            viewProperty: 'viewSecurity',
            editProperty: 'editSecurity',
            ruleSet: VesselNotificationCategoryCodes.Security
        },
        8: {
            viewPermission: VesselNotificationPermissions.View.wastes,
            editPermission: VesselNotificationPermissions.Edit.wastes,
            viewProperty: 'viewWastes',
            editProperty: 'editWastes',
            ruleSet: VesselNotificationCategoryCodes.Wastes
        },
        9: {
            viewPermission: VesselNotificationPermissions.View.mdh,
            editPermission: VesselNotificationPermissions.Edit.mdh,
            viewProperty: 'viewMdh',
            editProperty: 'editMdh',
            ruleSet: VesselNotificationCategoryCodes.Mdh
        },
        10: {
            viewPermission: VesselNotificationPermissions.View.cruiseItineraries,
            editPermission: VesselNotificationPermissions.Edit.cruiseItineraries,
            viewProperty: 'viewCruiseItineraries',
            editProperty: 'editCruiseItineraries',
            ruleSet: null
        },
        11: {
            viewPermission: VesselNotificationPermissions.View.attachments,
            editPermission: VesselNotificationPermissions.Edit.attachments,
            viewProperty: 'viewAttachments',
            editProperty: 'editAttachments',
            ruleSet: null
        },
        12: {
            viewPermission: VesselNotificationPermissions.View.accompanied,
            editPermission: VesselNotificationPermissions.Edit.accompanied,
            viewProperty: 'viewAccompanied',
            editProperty: 'editAccompanied',
            ruleSet: null
        },
        13: {
            viewPermission: VesselNotificationPermissions.View.logs,
            editPermission: VesselNotificationPermissions.Edit.logs,
            viewProperty: 'viewLogs',
            editProperty: 'editLogs',
            ruleSet: null
        },
        14: {
            viewPermission: VesselNotificationPermissions.View.permissions,
            editPermission: VesselNotificationPermissions.Edit.permissions,
            viewProperty: null,
            editProperty: null,
            ruleSet: null
        },
        15: { // Print
            viewPermission: VesselNotificationPermissions,
            editPermission: VesselNotificationPermissions,
            viewProperty: null,
            editProperty: null,
            ruleSet: null
        },
        16: { // General remarks
            viewPermission: VesselNotificationPermissions,
            editPermission: VesselNotificationPermissions,
            viewProperty: null,
            editProperty: null,
            ruleSet: null
        },
        17: { // Details
            viewPermission: VesselNotificationPermissions,
            editPermission: VesselNotificationPermissions,
            viewProperty: null,
            editProperty: null,
            ruleSet: null
        },
        18: { // Manifests
            viewPermission: VesselNotificationPermissions.View.manifests,
            editPermission: VesselNotificationPermissions.Edit.manifests,
            viewProperty: 'viewManifests',
            editProperty: 'editManifests',
            ruleSet: null
        },
        19: { //Cargo Data
            viewPermission: VesselNotificationPermissions.View.cargoData,
            editPermission: VesselNotificationPermissions.Edit.cargoData,
            viewProperty: 'viewCargoData',
            editProperty: 'editCargoData',
            ruleSet: null
        }
    };

    currentStep = 0;
    selectedTabIndex = 0;
    activeTab: string;
    finalStepIndex = 18;
    confirmed: boolean;
    validating;
    additionalAgents: string[] = [];
    cargoAgents: string[] = [];
    selectedVessel;
    searchData = [];
    actionBarStatus: any;

    searchFilterChanged = new Subject<any>();
    vesselSelectSortFields = [
        { columnName: 'IMO', field: 'iMONumber', sort: null },
        { columnName: 'MMSI', field: 'mmsi', sort: null },
        { columnName: 'Vessel Name', field: 'name', sort: false },
        { columnName: 'Flag', field: 'countryId', sort: null },
    ];

    @ViewChild('autocomplete', { static: false })
    public autocomplete: AutoCompleteComponent;

    get editMode() {
        return this.mode !== ViewMode.view;
    }

    get viewMode() {
        return this.mode === ViewMode.view;
    }

    get isCreate() {
        return this.mode === ViewMode.create;
    }

    @Input() model: any;
    @Input() modelId: number;
    @Input() entityManager: EntityManager;
    @Input() user: User;
    @Input() mode: ViewMode;
    @Input() canEdit: boolean;
    @Input() enableSteps: boolean;
    @Input() initialSelectedTabIndex: number;
    @Input() showHeader: boolean;
    @Input() isCargoData: boolean = false;

    @Output() confirm = new EventEmitter();
    @Output() tabSelected = new EventEmitter();

    @ViewChild('tabs', { static: false }) tabs: TabStripComponent;
    @ViewChild('additionalAgentsControl', { static: false }) additionalAgentsControl: AppControlComponent;
    @ViewChild('cargoAgentsControl', { static: false }) cargoAgentsControl: AppControlComponent;

    constructor(private translateService: TranslateService,
        private breezeViewService: BreezeViewService,
        private dialogService: DialogService,
        private router: Router,
        private injector: Injector,
        private sharedService: SharedService) {
        this.searchFilterChanged
            .pipe(debounceTime(500))
            .subscribe(filter => {
                this.searchData = [];
                this.autocomplete.loading = true;
                this.breezeViewService.handleQuery('VesselsList', { filter })
                    .then(results => {
                        _.each(results, (val, i) => val.index = i.toString());
                        this.searchData = results;
                        this.sortVesselSelect('name');
                    })
                    .finally(() => this.autocomplete.loading = false);
            });

        this.sharedService.on('action-bar-toggle', data => this.actionBarStatus = data);
    }

    async ngOnInit() {
        // If rendered in vessel visit view
        if (!this.model) [this.model] = await this.breezeViewService.handleQuery('VesselNotification', { id: this.modelId });
        this.modelLoaded();
        if (!this.enableSteps) {
            this.currentStep = null;
            this.selectedTabIndex = this.initialSelectedTabIndex && this.isVisible(this.initialSelectedTabIndex) ? this.initialSelectedTabIndex : 0;
        }
        this.entityManager.entityChanged.subscribe((entityData: EntityChangedEventArgs) => {
            if (entityData.entity.entityType.shortName === 'VesselNotificationAgent' && entityData.entityAction.name === 'EntityStateChange') {
                this.additionalAgents = this.model.additionalAgents.filter(x => x.agentRoleId === 'VA').map(a => a.agentId);
                this.cargoAgents = this.model.additionalAgents.filter(x => x.agentRoleId === 'CA').map(a => a.agentId);
            }
        });
    }

    modelLoaded() {
        this.additionalAgents = this.model.additionalAgents.filter(x => x.agentRoleId === 'VA').map(a => a.agentId);
        this.cargoAgents = this.model.additionalAgents.filter(x => x.agentRoleId === 'CA').map(a => a.agentId);
        if (!this.model.agent && this.model.agentId)
            this.model.entityAspect.loadNavigationProperty('agent');

        if (this.editMode) {
            const entityAspect = this.model.entityAspect;
            entityAspect.propertyChanged.subscribe((event) => {
                if (event.propertyName === 'primaryCallPurposeId') entityAspect.validateEntity();
            });
        }
    }

    async onTabSelect(event: SelectEvent) {
        if (this.validating) {
            event.preventDefault();
            return;
        }

        // Reached final tab
        if (this.finalStepIndex === this.currentStep) {
            // Clicked on the confirm TAB
            if (this.confirmed) {
                event.preventDefault();

                const res = await this.dialogService.openConfirmDialog('Save Vessel Notification',
                    this.translateService.instant('By pressing on Confirm the vessel notification will be saved.')
                );
                if (!res) {
                    this.confirmed = false;
                    return;
                }

                this.confirm.emit();
            }
            return;
        }

        if (this.canGoNext() && this.tabs.tabs.length === (event.index + 1)) {
            event.preventDefault();

            if (!await this.validate()) return;

            this.currentStep++;
            while (this.currentStep < this.finalStepIndex && !this.isVisible(this.currentStep) && await this.validate()) this.currentStep++;

            this.selectedTabIndex = this.currentStep;
        } else if (event.index <= this.currentStep || !this.isCreate) this.selectedTabIndex = event.index;

        this.tabSelected.emit(this.selectedTabIndex);
    }

    async validate(): Promise<boolean> {
        const ruleSet = VesselNotificationViewContentComponent.tabPermissions[this.currentStep].ruleSet;
        if (!ruleSet) return true;

        this.validating = true;
        try {
            await this.breezeViewService.saveChanges('ValidateVesselNotification', this.model, { ruleSet });
            return true;
        } catch { return false; }
        finally { this.validating = false; }
    }

    getStepIndex(tabIndex) {
        let stepIndex = -1;
        for (let i = 0; i <= 19; i++) {
            stepIndex++;

            if (stepIndex === tabIndex) return i;
        }

        return this.isCreate ? this.finalStepIndex : 0;
    }

    isSelected(index) {
        return this.getStepIndex(this.selectedTabIndex) === index;
    }

    isEditable(index, cargoAgentField = false, dutyNumberField = false) {
        if (!this.canEdit) return false;

        const organizationId = this.user?.organizationId;
        const tabPermission = VesselNotificationViewContentComponent.tabPermissions[index];
        const permission = tabPermission.editPermission;
        const permissionProperty = tabPermission.editProperty;
        const vnPermission = _.find(this.model.permissions, o => o.organizationId === organizationId);
        if (vnPermission && permissionProperty) return vnPermission[permissionProperty];

        if (!this.user?.hasPermission(permission)) return false;

        if (['D', 'W', 'A'].includes(this.model.statusId) && dutyNumberField && this.user?.isAgent())
            return true;

        if (this.model.statusId === 'DP' && this.model.notificationTypeId === 'D' && index === 8 && this.user?.isAgent()) {
            const lastEditableDate = new Date(this.model.vesselVisit?.atd);
            lastEditableDate.setDate(lastEditableDate.getDate() + 15);
            lastEditableDate.setHours(23, 59, 59, 999);
            return lastEditableDate > new Date();
        }

        if (cargoAgentField && ['COM', 'NOCA'].includes(this.model.manifestStatusId)) return false;

        if (this.model.statusId === 'DP' && this.model.notificationTypeId === 'D' && !([3, 6, 8, 12, 18, 19].includes(index) || cargoAgentField) && this.user?.isAgent())
            return false;

        if (['AR', 'A'].includes(this.model.statusId) && this.model.notificationTypeId === 'A' && !([6, 12, 18, 19].includes(index) || cargoAgentField) && this.user?.isAgent())
            return false;

        if (this.model.statusId === 'A' && this.model.notificationTypeId === 'D' && !([8, 18, 19].includes(index) || cargoAgentField) && this.user?.isAgent())
            return false;

        if ((this.model.healthApprovalDate && index === 9 || this.model.iSPSApprovalDate && index === 7) && this.user?.isAgent())
            return false;

        if (index === 0 && (this.user?.isSecurity() || this.user?.isHealth())) // Disable voyage edit for security users
            return false;

        if (index === 18 && this.model.additionalAgents.some(x => x.agentRoleId === 'CA' && x.agentId === this.user?.organizationId))
            return true;

        return !this.user?.isAgent() || // Malta transport can edit all
            this.model.agentId === organizationId; // Primary agent can edit all
    }

    isVisible(index) {
        if (index === 0 && this.currentStep === null /* Voyage */)
            return true;

        if (index === 17 /* Details - declarations */ && this.model.statusId !== 'X' && this.viewMode)
            return true;

        if (index === 13 /*Log*/ && this.editMode)
            return false;

        if (index === 8 /*Waste*/ && this.model.vessel.emsaExemption)
            return false;

        if ([15 /* Print */, 14 /* Permissions */].indexOf(index) >= 0)
            return !this.isCreate;

        if (this.model.notificationTypeId !== 'A' && [4/*Store*/, 7/*ISPS*/, 9/*Health*/].indexOf(index) >= 0)
            return false;

        if ([4/*Store*/, 8/*Waste*/].indexOf(index) >= 0 && this.model.portOfCallId === 'MTWTW')
            return false;

        if (index === 10 /* Itineraries */ && this.model.vessel?.typeId !== 37 /* Cruiser */)
            return false;

        if ([2, 3].includes(index) /* Cargo, DPG */ && this.model.vessel?.typeId === 114 /* Yacht */)
            return false;

        if (index === 18 && this.model.additionalAgents.filter(x => x.agentRoleId === 'CA').some(x => x.agentId === this.user?.organizationId))
            return true;

        const organizationId = this.user?.organizationId;
        const tabPermission = VesselNotificationViewContentComponent.tabPermissions[index];
        const permission = this.editMode ? tabPermission.editPermission : tabPermission.viewPermission;
        const permissionProperty = this.editMode ? tabPermission.editProperty : tabPermission.viewProperty;
        const vnPermission = _.find(this.model.permissions, o => o.organizationId === organizationId);
        if (vnPermission && permissionProperty) return vnPermission[permissionProperty];

        if (!this.user?.hasPermission(permission)) return false;

        if (this.currentStep === null)
            return !this.user?.isAgent() || // Malta transport can view all
                this.model.agentId === organizationId; // Primary agent can view all

        return index <= this.currentStep;
    }

    getTabClass(index) {
        return this.isSelected(index) ? 'text-warning' : 'text-success';
    }

    getDangerTabClass(index) {
        return this.isSelected(index) ? 'text-warning' : 'text-danger';
    }

    canGoNext() {
        if (!this.isCreate || this.getStepIndex(this.selectedTabIndex) !== this.currentStep) return false;

        return this.model.vessel != null;
    }

    onDetailsTabSelected(index) {
        if (!this.isVisible(index)) return;

        let tabIndex = 0;
        for (let i = 0; i < index; i++) {
            if (this.isVisible(i)) tabIndex++;
        }

        this.tabs.selectTab(tabIndex);
    }

    onAgentChange(value) {
        if (!value) return;

        this.model.agentId = value;
        this.model.entityAspect.loadNavigationProperty('agent');
        this.additionalAgentsControl?.applyFilter();
        this.cargoAgentsControl?.applyFilter();
    }

    onAdditionalAgentsChange(ids) {
        const modelIds = _.map(this.model.additionalAgents.filter(x => x.agentRoleId === 'VA'), (o, i) => o.agentId);
        // Add
        _.each(_.difference(ids, modelIds), id => {
            this.model.additionalAgents.push(this.model.entityAspect.entityManager
                .createEntity('VesselNotificationAgent', {
                    agentId: id,
                    vesselNotificationId: this.model.id,
                    agentRoleId: 'VA'
                }));
        });
        // Remove
        _.each(_.difference(modelIds, ids), id => {
            const entity = _.find(this.model.additionalAgents, o => o.agentId === id && o.agentRoleId === 'VA');
            entity.entityAspect.setDeleted();
        });
    }

    onCargoAgentsChange(ids) {
        const modelIds = _.map(this.model.additionalAgents.filter(x => x.agentRoleId === 'CA'), (o, i) => o.agentId);
        // Add
        _.each(_.difference(ids, modelIds), id => {
            this.model.additionalAgents.push(this.model.entityAspect.entityManager
                .createEntity('VesselNotificationAgent', {
                    agentId: id,
                    vesselNotificationId: this.model.id,
                    agentRoleId: 'CA'
                }));
        });
        // Remove
        _.each(_.difference(modelIds, ids), async id => {
            const res = await this.breezeViewService.dialogService.openConfirmDialog('Remove Cargo Agent Nomination', 'You are removing Cargo Agent nomination. This will remove all cargo manifest records added by this cargo agent.\n' +
                'Please confirm if you wish to proceed.');
            if (!res) {
                this.cargoAgents.push(id);
                this.cargoAgentsControl.hasValue = this.cargoAgents.length > 0;
                return;
            }
            const entity = _.find(this.model.additionalAgents, o => o.agentId === id && o.agentRoleId === 'CA');
            entity.entityAspect.setDeleted();
        });
    }

    excludePrimaryAgent(item) {
        return item.id !== this.model?.agentId;
    }

    onStepModalClosed(event: boolean, tab) {
        // Go to next tab
        if (!event) this.onTabSelect(new SelectEvent(this.selectedTabIndex + 1, ''));
    }

    canOpenCargoModal() {
        // for cargo if Primary Call purpose = 1 and call = LOA; Only relevant for Create and Edit
        return this.model.primaryCallPurposeId === 1 &&
            this.model.callActivityId === 'LOA' &&
            this.editMode;
    }

    canOpenHazmatModal() {
        return this.canOpenModal();
    }

    canOpenModal() {
        // For hazmat and bunkers
        return [1, 2, 3, 4, 5, 6].includes(this.model.primaryCallPurposeId) &&
            ['MTMAR', 'MTMLA', 'MTWTW'].includes(this.model.portOfCallId) &&
            this.editMode;
    }

    isTabVisible = (index) => this.isVisible(index);

    canImportIHSVessel() {
        return (this.user.hasPermission(VesselPermissions.Action.ihsImport) || this.user.hasPermission(VesselPermissions.Action.ihsImportAgent)) &&
            this.isCreate;
    }

    async importIHSVessel() {
        const result = await this.dialogService.open(ImportIhsVesselModalComponent, {
            injector: this.injector,
            size: 'xl'
        });
        if (result) {
            this.model.vesselId = result;
            this.model.entityAspect.loadNavigationProperty('vessel');
            this.model.notificationTypeId = 'A';
            this.model.entityAspect.validateEntity();
        }
    }

    canImportEMSAVessel() {
        return (this.user.hasPermission(VesselPermissions.Action.emsaImport) || this.user.hasPermission(VesselPermissions.Action.emsaImportAgent)) &&
            this.isCreate;
    }

    async importEMSAVessel() { }

    searchValueChange(value) {
        this.autocomplete.reset();
        const index = parseInt(value, null);
        if (isNaN(index)) return;

        this.selectedVessel = this.searchData[index];

        if (!this.selectedVessel) return;
    }

    searchFilterChange(filter) {
        if (filter.length >= 2) {
            this.searchFilterChanged.next(filter);
        } else {
            this.autocomplete.toggle(false);
            if (this.searchData.length) this.searchData = [];
        }
    }

    createArrival() {
        this.model.vessel = this.selectedVessel.vessel;
        this.model.notificationTypeId = 'A';
        this.model.entityAspect.validateEntity();
    }

    sortVesselSelect(field: string) {
        if (_.find(this.vesselSelectSortFields, vesselSort => vesselSort.field === field).sort) {
            this.searchData = this.searchData.sort((a, b) => (a.vessel[field] < b.vessel[field] ? 1 : -1));
            this.modifyVesselSelectSort(false, field);
        } else {
            this.searchData = this.searchData.sort((a, b) => (a.vessel[field] < b.vessel[field] ? -1 : 1));
            this.modifyVesselSelectSort(true, field);
        }
    }

    modifyVesselSelectSort(order: boolean, field: string) {
        _.forEach(this.vesselSelectSortFields, vesselSort => vesselSort.sort = vesselSort.field === field ? order : null);
    }

    currentlyOn(location): boolean {
        return this.router.url.split('/').indexOf(location) !== -1;
    }

    isDarkMode() {
        return JSON.parse(localStorage.getItem('dark'));
    }

    isDetained() {
        return this.model.vessel.statusId === 'DT';
    }

    isAlerted() {
        return this.model.vessel.statusId === 'BN';
    }

    isOpsConnected(vesselVisit: any) {
        return vesselVisit?.energyConsumptions.some(x => !x.disconnectionTimestamp);
    }
}
